// Roam the city (docs/plans/2026-10-19-fixes-round-1.md, section C): offline, on the district map, from a place you
// pick. The places are every metro station on the map (its OSM stops and stations) and the named landmarks the
// barricade survey found (data/places/landmarks.csv, written by tools/landmarks.py). Pure, so tests read the files.
import { GameMap, Poi, Vec, fieldAt, fieldFor } from './map';

export type RoamKind = 'metro' | 'landmark';
/** A place to start from. x, y in district tiles, like the CSV and the map file's pois; `at` is where you come up. */
export interface RoamPlace { id: string; name: string; kind: RoamKind; group: string; x: number; y: number; at: { x: number; y: number } }

/** The landmark kinds roam offers. Police posts and stations are left out: roam has no police. Metro comes from the map. */
const LANDMARK_KINDS: Record<string, string> = {
  parliament: 'Parliament', ministry: 'Ministries and bhawans', government: 'Offices', court: 'Courts', embassy: 'Embassies',
  heritage: 'Heritage', worship: 'Places of worship', culture: 'Culture', hotel: 'Hotels', park: 'Parks',
};
/** Rows that are a statue or memorial of a real person rather than a place to stand: the game draws no real person. */
const NOT_A_PLACE = new Set(['Mahatma Gandhi', 'Pope John-Paul II.', "Netaji Subhash Chandra Bose's canopy"]);

/** Railway halts OSM files with the metro (railway=station or stop with no subway): the Ring Railway's, not the metro's. */
const RAIL_HALTS = new Set(['Shivaji Bridge', 'Tilak Bridge', 'Delhi']);

/** One CSV line into fields: commas inside double quotes stay, "" is a quote. */
export function csvLine(line: string): string[] {
  const out: string[] = []; let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) { if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; } else if (c === '"') q = false; else cur += c; }
    else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out;
}

/** The name a player reads: the OSM name, unless it is in Devanagari only or cut short, then the English one. */
export function placeName(name: string, en: string): string {
  const n = name.trim(), e = en.trim();
  if (e && (/[ऀ-ॿ]/.test(n) || /\($/.test(n))) return e;
  return n.replace(/\s*\($/, '') || e;
}

/** The station's own name from an entrance's ("Rajiv Chowk Metro Gate No 2" -> "Rajiv Chowk"). */
const stationOf = (n: string) => n.replace(/\s+Metro\b.*$/i, '').trim();

/**
 * Every metro station on the map, once each: its stop and station nodes averaged, then the nearest of its entrances
 * (within 20 tiles) as where you come up, since a station node sits over the platform. `pois` in tiles, as in the file.
 */
export function stations(pois: Poi[]): RoamPlace[] {
  const by = new Map<string, Poi[]>();
  for (const p of pois) if (p.g === 'metro' && p.name && /railway=(station|stop)/.test(p.tag)) by.set(p.name, [...(by.get(p.name) ?? []), p]);
  const exits = pois.filter((p) => p.g === 'metro' && p.tag === 'railway=subway_entrance');
  const out: RoamPlace[] = [];
  for (const [name, ps] of by) {
    const x = ps.reduce((a, p) => a + p.x, 0) / ps.length, y = ps.reduce((a, p) => a + p.y, 0) / ps.length;
    // an entrance named for this station, or an unnamed one, nearest first
    const near = exits.filter((e) => !e.name || stationOf(e.name) === name).map((e) => ({ e, d: Math.hypot(e.x - x, e.y - y) })).filter((c) => c.d <= 20).sort((a, b) => a.d - b.d)[0];
    if (RAIL_HALTS.has(name)) continue;
    out.push({ id: `metro:${slug(name)}`, name, kind: 'metro', group: 'Metro stations', x, y, at: near ? { x: near.e.x, y: near.e.y } : { x, y } });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** The named landmarks on the district map, from the CSV text: one per name and spot (nodes for one building collapse). */
export function landmarks(csv: string): RoamPlace[] {
  const [head, ...lines] = csv.split(/\r?\n/).filter((l) => l.trim());
  const H = csvLine(head), col = (r: string[], k: string) => r[H.indexOf(k)] ?? '';
  const out: RoamPlace[] = [];
  for (const l of lines) {
    const r = csvLine(l), kind = col(r, 'kind'), x = Number(col(r, 'x')), y = Number(col(r, 'y'));
    if (col(r, 'on_district_map') !== '1' || !LANDMARK_KINDS[kind] || !Number.isFinite(x) || !Number.isFinite(y)) continue;
    const name = placeName(col(r, 'name'), col(r, 'name_en'));
    if (!name || NOT_A_PLACE.has(name)) continue;
    if (out.some((p) => p.name === name || Math.hypot(p.x - x, p.y - y) < 2)) continue; // the same place twice
    out.push({ id: `place:${slug(name)}`, name, kind: 'landmark', group: LANDMARK_KINDS[kind], x, y, at: { x, y } });
  }
  return out;
}

/** The picker's list: the stations, then the landmarks by group in the CSV's order (nearest the stage first). */
export function roamPlaces(pois: Poi[], csv: string): RoamPlace[] {
  return [...stations(pois), ...landmarks(csv)];
}

const slug = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Where to stand for a place (world px): the nearest open tile to it that can walk to the plaza, so a landmark inside
 * a walled block puts you on the street outside it. Searched outwards ring by ring, up to `within` tiles.
 */
export function roamSpot(m: GameMap, p: Vec, within = 60): Vec | null {
  const F = fieldFor(m, 'plaza', () => [m.plaza]);
  const tx = Math.floor(p.x / m.tile), ty = Math.floor(p.y / m.tile);
  const ok = (x: number, y: number) => x >= 0 && y >= 0 && x < m.w && y < m.h && F[y * m.w + x] >= 0;
  if (fieldAt(m, F, p.x, p.y) >= 0) return { x: p.x, y: p.y };
  for (let r = 1; r <= within; r++) {
    let best: Vec | null = null, bd = Infinity;
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== r || !ok(tx + dx, ty + dy)) continue;
      const c = { x: (tx + dx + 0.5) * m.tile, y: (ty + dy + 0.5) * m.tile }, d = Math.hypot(c.x - p.x, c.y - p.y);
      if (d < bd) { bd = d; best = c; }
    }
    if (best) return best;
  }
  return null;
}
